import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useIntl } from "react-intl";
import {
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Pagination,
  PaginationItem,
  PaginationLink,
  Table,
} from "reactstrap";
import logo from "../../../assets/wembii-logo.png";
import { ChartMetric } from "../../../cores/analytics/interface/ChartMetric";
import { SummaryLineChart } from "./SummaryLineChart";
import {
  SUMMARY_PLATFORM_COLORS,
  SUMMARY_PLATFORM_LABEL_KEYS,
} from "./summary/summaryPlatformColors";
import {
  SummaryAnalyticsSnapshot,
  SummaryMetricBlock,
  SummaryPlatform,
  SummaryPostRow,
  SummaryTrendDir,
} from "./summary/summaryAnalyticsTypes";
import "./SummaryAnalytics.scss";

interface Props {
  snapshot: SummaryAnalyticsSnapshot;
  companyName?: string;
  reportMode?: boolean;
  logoUrl?: string;
}

type SummaryTab = "account" | "posts";

const formatCompact = (n: number): string => {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
  }
  if (abs >= 10_000) {
    return `${(n / 1_000).toFixed(2).replace(/\.?0+$/, "")}k`;
  }
  if (abs >= 1_000) {
    return new Intl.NumberFormat("es-CL", { maximumFractionDigits: 0 }).format(
      Math.round(n)
    );
  }
  return new Intl.NumberFormat("es-CL", { maximumFractionDigits: 0 }).format(n);
};

const TREND_ICONS: Record<SummaryTrendDir, IconProp> = {
  up: ["fas", "arrow-up"],
  down: ["fas", "arrow-down"],
  flat: ["fas", "minus"],
};

const trendIcon = (dir?: SummaryTrendDir): IconProp =>
  TREND_ICONS[dir ?? "flat"];

const PLATFORM_ICONS: Record<SummaryPlatform, IconProp> = {
  facebook: ["fab", "facebook-f"],
  instagram: ["fab", "instagram"],
  tiktok: ["fab", "tiktok"],
};

const METRIC_ICONS: Record<string, IconProp> = {
  "app.analytics.summary.followers": ["fas", "users"],
  "app.analytics.summary.interactions": ["fas", "heart"],
  "app.analytics.summary.impressions": ["fas", "eye"],
  "app.analytics.summary.publications": ["fas", "photo-video"],
};

const blockToChart = (block: SummaryMetricBlock): ChartMetric => {
  const platforms = block.platforms.map((p) => p.platform);
  return {
    labels: block.series.map((s) => s.label),
    datasets: platforms.map((platform) => ({
      label: platform,
      color: SUMMARY_PLATFORM_COLORS[platform],
      data: block.series.map((s) => s.values[platform] ?? 0),
      yAxisID: "left",
      fill: false,
      type: "line" as const,
    })),
  };
};

function MetricSection(props: {
  titleId: string;
  block: SummaryMetricBlock;
  syncedPlatforms: SummaryPlatform[];
  sectionId?: string;
  forceLightTheme?: boolean;
}) {
  const intl = useIntl();
  const { block, syncedPlatforms } = props;
  const total = block.total;
  const segments = block.platforms.filter((p) => p.value > 0);
  const chart = useMemo(() => blockToChart(block), [block]);

  return (
    <section id={props.sectionId} className="summary-analytics__section">
      <div className="summary-analytics__metric-panel">
        <div className="summary-analytics__metric-card">
          <div className="summary-analytics__metric-icon">
            <FontAwesomeIcon icon={METRIC_ICONS[props.titleId] || ["fas", "chart-line"]} />
          </div>
          <div className="summary-analytics__metric-content">
            <div className="summary-analytics__metric-kicker">
              {intl.formatMessage({ id: props.titleId })}
            </div>
            <div className="summary-analytics__metric-head">
              <span className="summary-analytics__metric-total">
                {formatCompact(total)}
              </span>
              {block.direction ? (
                <span
                  className={cn(
                    "summary-analytics__metric-trend",
                    `summary-analytics__metric-trend--${block.direction}`
                  )}
                >
                  <FontAwesomeIcon icon={trendIcon(block.direction)} />
                </span>
              ) : null}
            </div>
          </div>
        </div>
        {total > 0 && segments.length > 0 ? (
          <div className="summary-analytics__distribution-bar">
            {segments.map((seg) => (
              <div
                key={seg.platform}
                className="summary-analytics__distribution-segment"
                style={{
                  width: `${(seg.value / total) * 100}%`,
                  backgroundColor: SUMMARY_PLATFORM_COLORS[seg.platform],
                }}
              />
            ))}
          </div>
        ) : null}

        <div className="summary-analytics__platform-cards">
          {syncedPlatforms.map((platform) => {
            const item = block.platforms.find((p) => p.platform === platform);
            const value = item?.value ?? 0;
            const share = total > 0 ? Math.round((value / total) * 100) : 0;
            return (
              <div key={platform} className="summary-analytics__platform-card">
                <div
                  className="summary-analytics__platform-icon"
                  style={{ backgroundColor: SUMMARY_PLATFORM_COLORS[platform] }}
                >
                  <FontAwesomeIcon icon={PLATFORM_ICONS[platform]} />
                </div>
                <div className="summary-analytics__platform-body">
                  <div className="summary-analytics__platform-label">
                    {intl.formatMessage({ id: SUMMARY_PLATFORM_LABEL_KEYS[platform] })}
                  </div>
                  <div className="summary-analytics__platform-value">
                    {formatCompact(value)}
                    {item?.direction ? (
                      <FontAwesomeIcon
                        icon={trendIcon(item.direction)}
                        className={cn(
                          "summary-analytics__platform-trend",
                          `summary-analytics__platform-trend--${item.direction}`
                        )}
                      />
                    ) : null}
                  </div>
                </div>
                <span className="summary-analytics__platform-share">{share}%</span>
              </div>
            );
          })}
        </div>

        {block.series.length > 0 ? (
          <div className="summary-analytics__chart-wrap">
            <div className="summary-analytics__chart-head">
              <span>{intl.formatMessage({ id: props.titleId })}</span>
              <div className="summary-analytics__legend">
                {syncedPlatforms.map((platform) => (
                  <span key={platform} className="summary-analytics__legend-item">
                    <i style={{ backgroundColor: SUMMARY_PLATFORM_COLORS[platform] }} />
                    {intl.formatMessage({ id: SUMMARY_PLATFORM_LABEL_KEYS[platform] })}
                  </span>
                ))}
              </div>
            </div>
            <SummaryLineChart {...chart} forceLightTheme={props.forceLightTheme} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

function PostsTable(props: {
  posts: SummaryPostRow[];
  syncedPlatforms: SummaryPlatform[];
  sectionId?: string;
  captureMode?: boolean;
}) {
  const intl = useIntl();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return props.posts;
    return props.posts.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.typeLabel.toLowerCase().includes(q) ||
        p.platform.includes(q)
    );
  }, [props.posts, search]);

  useEffect(() => {
    setPage(1);
  }, [filtered.length, search]);

  const visiblePosts = props.captureMode ? filtered.slice(0, 10) : filtered;
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const slice = props.captureMode
    ? visiblePosts
    : filtered.slice((safePage - 1) * perPage, safePage * perPage);

  const csvData = filtered.map((p) => ({
    Plataforma: p.platform,
    Título: p.title,
    Tipo: p.typeLabel,
    Fecha: p.dateLabel,
    Hora: p.timeLabel,
    Impresiones: p.impressions,
  }));

  return (
    <section id={props.sectionId} className="summary-analytics__section">
      <div className="summary-analytics__table-panel">
      <div className="summary-analytics__table-head">
        <div>
          <h3 className="summary-analytics__metric-title mb-1">
            {intl.formatMessage({ id: "app.analytics.summary.postList" })}
          </h3>
          <span className="summary-analytics__table-count">
            {filtered.length} {intl.formatMessage({ id: "app.analytics.summary.publications" }).toLowerCase()}
          </span>
        </div>
        {!props.captureMode ? (
          <CSVLink
            data={csvData}
            filename="publicaciones-resumen.csv"
            className="summary-analytics__csv-button"
          >
            <FontAwesomeIcon icon={["fas", "file-csv"]} />
            {intl.formatMessage({ id: "app.analytics.summary.downloadCsv" })}
          </CSVLink>
        ) : null}
      </div>
      {!props.captureMode ? (
        <div className="summary-analytics__posts-toolbar">
          <InputGroup className="summary-analytics__posts-search">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                <FontAwesomeIcon icon={["fas", "search"]} />
              </InputGroupText>
            </InputGroupAddon>
            <Input
              placeholder={intl.formatMessage({ id: "app.analytics.summary.search" })}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </InputGroup>
        </div>
      ) : null}

      {filtered.length === 0 ? (
        <div className="summary-analytics__empty">
          {intl.formatMessage({ id: "app.analytics.summary.noPosts" })}
        </div>
      ) : (
        <>
          <Table responsive className="summary-analytics__posts-table">
            <thead>
              <tr>
                <th>{intl.formatMessage({ id: "app.analytics.summary.colPost" })}</th>
                <th>{intl.formatMessage({ id: "app.analytics.summary.colType" })}</th>
                <th>{intl.formatMessage({ id: "app.analytics.summary.colDate" })}</th>
                <th>{intl.formatMessage({ id: "app.analytics.summary.colImpressions" })}</th>
              </tr>
            </thead>
            <tbody>
              {slice.map((post) => (
                <tr key={post.id}>
                  <td>
                    <div className="summary-analytics__post-title">
                      {post.thumbnailUrl ? (
                        <img
                          src={post.thumbnailUrl}
                          alt=""
                          className="summary-analytics__post-thumb"
                        />
                      ) : (
                        <div
                          className="summary-analytics__post-thumb-placeholder"
                          style={{ color: SUMMARY_PLATFORM_COLORS[post.platform] }}
                        >
                          <FontAwesomeIcon icon={PLATFORM_ICONS[post.platform]} />
                        </div>
                      )}
                      <div>
                        <span>{post.title}</span>
                        <div className="summary-analytics__post-platform">
                          <i style={{ backgroundColor: SUMMARY_PLATFORM_COLORS[post.platform] }} />
                          {intl.formatMessage({ id: SUMMARY_PLATFORM_LABEL_KEYS[post.platform] })}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{post.typeLabel}</td>
                  <td>
                    <div>{post.dateLabel}</div>
                    <small className="text-muted">{post.timeLabel}</small>
                  </td>
                  <td>{formatCompact(post.impressions)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {!props.captureMode ? (
          <div className="summary-analytics__table-pagination d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">
                {intl.formatMessage({ id: "app.analytics.summary.perPage" })}
              </span>
              <Input
                type="select"
                bsSize="sm"
                style={{ width: 70 }}
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[5, 10, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Input>
              <span className="text-muted small">
                {(safePage - 1) * perPage + 1}-
                {Math.min(safePage * perPage, filtered.length)}{" "}
                {intl.formatMessage({ id: "app.analytics.summary.of" })}{" "}
                {filtered.length}
              </span>
            </div>
            <Pagination size="sm" className="mb-0">
              <PaginationItem disabled={safePage <= 1}>
                <PaginationLink first onClick={() => setPage(1)} />
              </PaginationItem>
              <PaginationItem disabled={safePage <= 1}>
                <PaginationLink previous onClick={() => setPage(safePage - 1)} />
              </PaginationItem>
              <PaginationItem disabled={safePage >= totalPages}>
                <PaginationLink next onClick={() => setPage(safePage + 1)} />
              </PaginationItem>
              <PaginationItem disabled={safePage >= totalPages}>
                <PaginationLink last onClick={() => setPage(totalPages)} />
              </PaginationItem>
            </Pagination>
          </div>
          ) : null}
        </>
      )}
      </div>
    </section>
  );
}

function HeroKpi(props: {
  labelId: string;
  value: number;
  icon: IconProp;
  tone: "followers" | "interactions" | "impressions";
}) {
  const intl = useIntl();
  return (
    <div className={cn("summary-analytics__hero-kpi", `summary-analytics__hero-kpi--${props.tone}`)}>
      <div className="summary-analytics__hero-kpi-icon">
        <FontAwesomeIcon icon={props.icon} />
      </div>
      <div>
        <span>{intl.formatMessage({ id: props.labelId })}</span>
        <strong>{formatCompact(props.value)}</strong>
      </div>
    </div>
  );
}

export const SummaryAnalyticsDashboard = ({
  snapshot,
  companyName,
  reportMode = false,
  logoUrl,
}: Props) => {
  const intl = useIntl();
  const [tab, setTab] = useState<SummaryTab>("account");

  if (!snapshot.syncedPlatforms.length) {
    return (
      <div className="summary-analytics__empty">
        {intl.formatMessage({ id: "app.analytics.summary.noPlatforms" })}
      </div>
    );
  }

  return (
    <div
      id="summaryReportRoot"
      className={cn("summary-analytics", {
        "summary-analytics--report-mode": reportMode,
      })}
    >
      <div id="summaryReportHeader" className="summary-analytics__report-header">
        <img
          src={logoUrl || logo}
          alt="Wembii"
          className="summary-analytics__report-logo"
        />
        <div>
          <h4>{intl.formatMessage({ id: "app.analytics.summary.title" })}</h4>
          <p>
            {companyName ? `${companyName} · ` : ""}
            {snapshot.periodLabel}
          </p>
        </div>
      </div>

      <div id="summaryReportOverview" className="summary-analytics__hero">
        <div className="summary-analytics__hero-copy">
          <span className="summary-analytics__hero-eyebrow">
            {snapshot.syncedPlatforms.length}{" "}
            {intl.formatMessage({ id: "app.analytics.summary.connectedNetworks" })}
          </span>
          <h3>{intl.formatMessage({ id: "app.analytics.summary.title" })}</h3>
          <p>{snapshot.periodLabel}</p>
        </div>
        <div className="summary-analytics__hero-kpis">
          <HeroKpi
            labelId="app.analytics.summary.followers"
            value={snapshot.account.followers.total}
            icon={["fas", "users"]}
            tone="followers"
          />
          <HeroKpi
            labelId="app.analytics.summary.interactions"
            value={snapshot.posts.interactions.total}
            icon={["fas", "heart"]}
            tone="interactions"
          />
          <HeroKpi
            labelId="app.analytics.summary.impressions"
            value={snapshot.posts.impressions.total}
            icon={["fas", "eye"]}
            tone="impressions"
          />
        </div>
      </div>
      {!reportMode ? (
      <div className="summary-analytics__header">
        <div className="summary-analytics__tabs">
          <button
            type="button"
            className={cn("summary-analytics__tab", {
              "summary-analytics__tab--active": tab === "account",
            })}
            onClick={() => setTab("account")}
          >
            {intl.formatMessage({ id: "app.analytics.summary.tabAccount" })}
          </button>
          <button
            type="button"
            className={cn("summary-analytics__tab", {
              "summary-analytics__tab--active": tab === "posts",
            })}
            onClick={() => setTab("posts")}
          >
            {intl.formatMessage({ id: "app.analytics.summary.tabPosts" })}
          </button>
        </div>
      </div>
      ) : null}

      <div
        className={cn("summary-analytics__tab-panel", {
          "summary-analytics__tab-panel--active": reportMode || tab === "account",
        })}
      >
        <MetricSection
          sectionId="summaryReportFollowers"
          titleId="app.analytics.summary.followers"
          block={snapshot.account.followers}
          syncedPlatforms={snapshot.syncedPlatforms}
          forceLightTheme={reportMode}
        />
      </div>

      <div
        className={cn("summary-analytics__tab-panel", {
          "summary-analytics__tab-panel--active": reportMode || tab === "posts",
        })}
      >
        <MetricSection
          sectionId="summaryReportInteractions"
          titleId="app.analytics.summary.interactions"
          block={snapshot.posts.interactions}
          syncedPlatforms={snapshot.syncedPlatforms}
          forceLightTheme={reportMode}
        />
        <MetricSection
          sectionId="summaryReportImpressions"
          titleId="app.analytics.summary.impressions"
          block={snapshot.posts.impressions}
          syncedPlatforms={snapshot.syncedPlatforms}
          forceLightTheme={reportMode}
        />
        <MetricSection
          sectionId="summaryReportPublications"
          titleId="app.analytics.summary.publications"
          block={snapshot.posts.publications}
          syncedPlatforms={snapshot.syncedPlatforms}
          forceLightTheme={reportMode}
        />
        <div className="summary-analytics__posts-visible">
          <PostsTable
            posts={snapshot.posts.postList}
            syncedPlatforms={snapshot.syncedPlatforms}
          />
        </div>
        <div id="summaryReportPosts" className="summary-analytics__report-source">
          <PostsTable
            posts={snapshot.posts.postList}
            syncedPlatforms={snapshot.syncedPlatforms}
            captureMode
          />
        </div>
      </div>
    </div>
  );
};
